import { ThemeToggleButton } from "@/context/ThemeToggleButton"
import { getPrimaryMenuItems, MenuItem, PrimaryMenuItem } from "@/lib/menu"
import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import {
  Bars3Icon,
  ChevronDownIcon,
  LightBulbIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline"
import * as NavigationMenu from "@radix-ui/react-navigation-menu"
import { AnimatePresence } from "framer-motion"
import { NavbarSearch } from "../search/NavbarSearch"
import { MobileMenu } from "./MobileMenu"
import { NavbarProps } from "./Navbar"

type ActiveNav = string | null

// Get the appropriate icon for each menu section
const getMenuIcon = (key: string) => {
  switch (key) {
    case "insights":
      return <LightBulbIcon className="h-10 w-10" />
    case "services":
      return <WrenchScrewdriverIcon className="h-10 w-10" />
    case "about":
      return <UserGroupIcon className="h-10 w-10" />
    default:
      return (
        <svg
          width="38"
          height="38"
          viewBox="0 0 25 25"
          fill="currentColor"
        >
          <path d="M12 25C7.58173 25 4 21.4183 4 17C4 12.5817 7.58173 9 12 9V25Z"></path>
          <path d="M12 0H4V8H12V0Z"></path>
          <path d="M17 8C19.2091 8 21 6.20914 21 4C21 1.79086 19.2091 0 17 0C14.7909 0 13 1.79086 13 4C13 6.20914 14.7909 8 17 8Z"></path>
        </svg>
      )
  }
}

// Right section: nav menu + utilities
export const NavbarMenu = (props: NavbarProps) => {
  const { themeSetting, toggleTheme } = props
  const pathname = usePathname()
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobileMenuOpen) {
      setMobileMenuOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isMobileMenuOpen])

  // Determine which nav item should be active
  const getActiveNavItem = (): ActiveNav => {
    const pathnameWithWhatever = pathname + "/whatever"
    const match = getPrimaryMenuItems().find(i => {
      return (
        i.href === pathname ||
        (i.href !== "/" && pathnameWithWhatever.startsWith(i.href))
      )
    })
    return match?.key ?? null
  }

  const currentActiveNavItem: ActiveNav = getActiveNavItem()

  // Helpers
  const linkClasses = (key: string) =>
    `relative py-1.5 font-medium transition-all ${
      currentActiveNavItem === key
        ? "text-brand"
        : "text-default hover:text-primary-hover"
    }`

  const NavigationSubMenuItem = ({ subPage }: { subPage: MenuItem }) => {
    const isActive = pathname === subPage.href

    return (
      <li>
        <NavigationMenu.Link asChild>
          <Link
            href={subPage.href}
            className="block select-none rounded-[6px] p-3 text-[15px] leading-none no-underline outline-none transition-colors hover:bg-surface-secondary focus:shadow-[0_0_0_2px] focus:shadow-primary"
          >
            <h4
              className={`mb-1 font-semibold ${isActive ? "text-primary" : ""}`}
            >
              {subPage.label}
            </h4>
            <p className="text-sm text-tertiary leading-[1.4]">
              {subPage.description}
            </p>
          </Link>
        </NavigationMenu.Link>
      </li>
    )
  }

  const NavigationSubMenu = ({ item }: { item: PrimaryMenuItem }) => {
    return (
      <div className="flex w-[500px]">
        {/* Featured callout on the left */}
        <div className="relative w-[220px]">
          <NavigationMenu.Link
            asChild
            className="h-full"
          >
            <Link
              href={item.href}
              className="block h-full"
            >
              {item.image ? (
                <Image
                  src={item.image.url}
                  alt={item.label}
                  width={item.image.width}
                  height={item.image.height}
                  className="absolute inset-0 h-full w-full object-cover"
                  priority={item.key === "home"}
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-surface-secondary text-brand">
                  {getMenuIcon(item.key)}
                </div>
              )}
              {/* Gradient overlay for text readability */}
              {!item.image?.disableOverlay && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/0" />
              )}
              <div className="relative flex h-full flex-col justify-end p-5 text-white">
                <h3 className="mb-2 text-lg font-semibold">{item.label}</h3>
                <p className="text-sm leading-relaxed text-white/80">
                  {item.description}
                </p>
              </div>
            </Link>
          </NavigationMenu.Link>
        </div>

        {/* Vertical Divider */}
        <div className="w-px bg-border" />

        {/* Menu items grid on the right */}
        <ul className="m-0 grid flex-1 list-none grid-cols-1 gap-x-2 gap-y-1 p-4">
          {item.subPages!.map(sub => (
            <NavigationSubMenuItem
              key={sub.key}
              subPage={sub}
            />
          ))}
        </ul>
      </div>
    )
  }

  const navigationMenuItem = (item: PrimaryMenuItem) => {
    const menuItemClasses = "NavigationMenuItem flex items-center"
    const isActive = currentActiveNavItem === item.key

    // --- CRITICAL: Radix UI Composition ---
    // Radix UI NavigationMenu requires a strict component structure for its animation
    // and positioning logic to work correctly. The direct children of a
    // <NavigationMenu.Item> *must* be Radix primitives like <NavigationMenu.Trigger>,
    // <NavigationMenu.Link>, and <NavigationMenu.Content>.
    //
    // Any other elements (e.g., for styling, like the active underline) must be
    // placed *inside* these primitives, typically within a Link passed to `asChild`.
    // Violating this structure will break the indicator's positioning.

    if (item.subPages) {
      // The `group` class allows the chevron to react to hover on the trigger
      const triggerClasses =
        linkClasses(item.key) + " group relative flex items-center gap-1"
      return (
        <NavigationMenu.Item
          key={item.key}
          className={menuItemClasses}
        >
          <NavigationMenu.Trigger asChild>
            <Link
              href={item.href}
              className={triggerClasses}
            >
              {item.label}
              <ChevronDownIcon
                className="relative top-[1px] ml-1 h-4 w-4 text-tertiary transition-transform duration-200 ease-in group-data-[state=open]:rotate-180"
                aria-hidden="true"
              />
              {isActive && (
                <div
                  className="absolute left-0 right-0 h-[2px] bg-primary"
                  style={{ bottom: "2px" }}
                />
              )}
            </Link>
          </NavigationMenu.Trigger>
          <NavigationMenu.Content className="absolute top-0 left-0 w-full sm:w-auto">
            <NavigationSubMenu item={item} />
          </NavigationMenu.Content>
        </NavigationMenu.Item>
      )
    }

    // Non-submenu items must also follow the strict composition rules.
    // The active state underline is inside the Link, and alignment is handled
    // with padding on the link itself, not with a sibling spacer div.
    const nonSubMenuLinkClasses = linkClasses(item.key) + " relative pr-[6px]"
    return (
      <NavigationMenu.Item
        key={item.key}
        className={menuItemClasses}
      >
        <NavigationMenu.Link asChild>
          <Link
            href={item.href}
            className={nonSubMenuLinkClasses}
          >
            {item.label}
            {isActive && (
              <div
                className="absolute left-0 right-0 h-[2px] bg-primary"
                style={{ bottom: "1px" }}
              />
            )}
          </Link>
        </NavigationMenu.Link>
      </NavigationMenu.Item>
    )
  }

  const NavbarNavigationMenu = () => {
    return (
      <NavigationMenu.Root
        className="navmenu relative hidden md:flex items-center gap-3 font-medium"
        delayDuration={200}
        orientation="horizontal"
      >
        <NavigationMenu.List className="NavigationMenuList flex items-center gap-3 font-medium flex-nowrap whitespace-nowrap">
          {getPrimaryMenuItems().map(navigationMenuItem)}

          {/* Radix arrow indicator */}
          <NavigationMenu.Indicator className="NavigationMenuIndicator">
            <div className="Arrow">&bull;</div>
          </NavigationMenu.Indicator>
        </NavigationMenu.List>

        {/* Viewport positioned outside the list */}
        <div className="ViewportPosition">
          <NavigationMenu.Viewport className="NavigationMenuViewport" />
        </div>
      </NavigationMenu.Root>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {/* Desktop Menu */}
      <NavbarNavigationMenu />

      <NavbarSearch />
      <ThemeToggleButton
        theme={themeSetting}
        toggleTheme={toggleTheme}
      />
      {/* Mobile Menu Button */}
      <button
        className="flex h-10 w-10 items-center justify-center rounded-md p-1 text-tertiary transition-colors hover:text-default md:hidden"
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Open menu"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && <MobileMenu setOpen={setMobileMenuOpen} />}
      </AnimatePresence>
    </div>
  )
}
